﻿import React, { useEffect } from "react";
import { useState } from "react";
import { Typography, TextField, Grid, Autocomplete } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchItemCodes,
  selectEditCatalogData,
  selectIsEditMode,
  selectAddEditConnectionInfo,
  setCatalogDataField,
  selectAvailableItems,
} from "@/features/connections/manageCatalogDataSlice";
import { defaultCode, blankCode } from "@/features/connections/integrationSlice";
import { AddEditCatalogPopup } from "./CatalogPopup";

const AddItemUOMPopup = ({ onClose, onValidate, onComplete }) => {
  const dispatch = useDispatch();
  const dataValues = useSelector(selectEditCatalogData);
  const isEdit = useSelector(selectIsEditMode);
  const [selectedItem, setItem] = useState({
    ArtifactDictionaryDataId: dataValues.ItemId,
    Code: dataValues.ItemCode ?? "None",
  });
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const itemCodes = useSelector(selectAvailableItems);

  useEffect(() => {
    if (connectionInfo.connectionId !== -1) {
      dispatch(
        fetchItemCodes({ selectedConnectionId: connectionInfo.connectionId }),
      );
    }
  }, [connectionInfo, dispatch]);

  useEffect(() => {
    if (selectedItem.ArtifactDictionaryDataId !== dataValues.ItemId) {
      setItem({
        ArtifactDictionaryDataId: dataValues.ItemId,
        Code: dataValues.ItemCode ?? "None",
        Description: "",
      });
    }
  }, [dataValues]);

  const handleItemChange = (newItem) => {
    if (newItem && newItem.Code !== "None") {
      dispatch(
        setCatalogDataField({
          name: "ItemCode",
          value: newItem.Code,
        }),
      );
      dispatch(
        setCatalogDataField({
          name: "ItemId",
          value: newItem.ArtifactDictionaryDataId,
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "ItemCode", value: null }));
      dispatch(setCatalogDataField({ name: "ItemId", value: null }));
    }
  };

  const validateForm = () => {
    if (onValidate) {
      const result = onValidate(dataValues);
      if (!result.isValid) return result;
    }
    //can no longer be in this state naturally, "None" is now the default option, not nothing selected
    if (selectedItem == null) {
      return {
        isValid: false,
        message: "Please select an option from the Item menu.",
      };
    }
    if (dataValues.UOM === defaultCode) {
      return {
        isValid: false,
        message: `UOM Code cannot be saved as ${defaultCode}`,
      };
    }
    if (dataValues.UOM === blankCode) {
      return {
        isValid: false,
        message: `UOM Code cannot be saved as ${blankCode}`,
      };
    }
    if (dataValues.UOM?.includes(",")) {
      return {
        isValid: false,
        message: `The UOM Code cannot include a ','`,
      };
    }
    if (
      dataValues.BaseFactor &&
      !Number.isFinite(Number(dataValues.BaseFactor))
    ) {
      return {
        isValid: false,
        message: "Item Base UOM Factor should be a valid number.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setItem({
      ArtifactDictionaryDataId: null,
      Code: "None",
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  const list = Array.isArray(itemCodes) ? itemCodes : [];
  const options = [{ Code: "None", Description: "" }, ...list];

  return (
    <AddEditCatalogPopup
      onClose={handleClose}
      onValidate={validateForm}
      onComplete={onComplete}
    >
      <Grid item xs={4}>
        <Typography>
          Item Code
          <Typography
            component="span"
            sx={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
          >
            *
          </Typography>
        </Typography>
        <Autocomplete
          disabled={isEdit}
          options={options}
          getOptionLabel={(option) =>
            option.Code === "None"
              ? "None"
              : `${option.Code ?? ""} ${option.Description ?? ""}`
          }
          isOptionEqualToValue={(option, value) => option.Code === value.Code}
          value={selectedItem}
          onChange={(e, newValue) => {
            handleItemChange(newValue || null);
          }}
          renderInput={(params) => (
            <TextField
              sx={{ marginTop: 1, marginBottom: 2, maxWidth: 250 }}
              {...params}
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>UOM Code</Typography>
        <Typography>
          <TextField
            disabled={isEdit}
            variant="outlined"
            name="UOM"
            value={dataValues?.UOM || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Item Base UOM Factor</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="BaseFactor"
            value={dataValues?.BaseFactor || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Item Base UOM</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="BaseUOM"
            value={dataValues?.BaseUOM || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={4} />
    </AddEditCatalogPopup>
  );
};

export default AddItemUOMPopup;
